import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as Tabs from '@radix-ui/react-tabs';
import { Settings, Building2, CalendarClock } from 'lucide-react';
import { useAuthStore, ROL } from '@/store/auth.store';
import { ClinicaPage } from '@/pages/clinicas/ClinicaPage';
import { BloquesHorarioPage } from '@/pages/agenda/BloquesHorarioPage';
import { ConfiguracionPage } from './ConfiguracionPage';

const ADMIN_ROLES = [ROL.SUPER_ADMIN, ROL.ADMIN_CLINICA, ROL.MEDICO_ADMIN];

type TabValue = 'clinica' | 'horarios' | 'sistema';
const TAB_VALUES: TabValue[] = ['clinica', 'horarios', 'sistema'];

const TAB_CLS = [
  'px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap flex items-center gap-1.5',
  'text-text-secondary border-transparent hover:text-text-primary',
  'data-[state=active]:text-primary-600 data-[state=active]:border-primary-500',
].join(' ');

export function AjustesPage() {
  const { usuario } = useAuthStore();
  const isAdmin = !!(usuario?.rolId && (ADMIN_ROLES as string[]).includes(usuario.rolId));

  const [searchParams, setSearchParams] = useSearchParams();

  function resolveTab(requested: string | null): TabValue {
    const defaultTab: TabValue = isAdmin ? 'clinica' : 'horarios';
    if (!requested || !TAB_VALUES.includes(requested as TabValue)) return defaultTab;
    if (requested !== 'horarios' && !isAdmin) return defaultTab;
    return requested as TabValue;
  }

  const [activeTab, setActiveTab] = useState<TabValue>(() => resolveTab(searchParams.get('tab')));

  function handleTabChange(value: string) {
    setActiveTab(value as TabValue);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', value);
      return next;
    }, { replace: true });
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0">
          <Settings size={20} className="text-primary-600" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-text-primary">Ajustes</h2>
          <p className="text-sm text-text-secondary mt-0.5">Clínica, horarios y sistema en un solo lugar</p>
        </div>
      </div>

      <Tabs.Root value={activeTab} onValueChange={handleTabChange}>
        <Tabs.List className="flex border-b border-gray-100 overflow-x-auto">
          {isAdmin && (
            <Tabs.Trigger value="clinica" className={TAB_CLS}>
              <Building2 size={14} /> Clínica
            </Tabs.Trigger>
          )}
          <Tabs.Trigger value="horarios" className={TAB_CLS}>
            <CalendarClock size={14} /> Horarios
          </Tabs.Trigger>
          {isAdmin && (
            <Tabs.Trigger value="sistema" className={TAB_CLS}>
              <Settings size={14} /> Sistema
            </Tabs.Trigger>
          )}
        </Tabs.List>

        {isAdmin && (
          <Tabs.Content value="clinica" className="pt-5 outline-none">
            <ClinicaPage />
          </Tabs.Content>
        )}

        <Tabs.Content value="horarios" className="pt-5 outline-none">
          <BloquesHorarioPage />
        </Tabs.Content>

        {isAdmin && (
          <Tabs.Content value="sistema" className="pt-5 outline-none">
            <ConfiguracionPage />
          </Tabs.Content>
        )}
      </Tabs.Root>
    </div>
  );
}
